import React, { useEffect, useMemo, useState } from 'react';
import { useMessage } from '../../hooks/useMessage';
import { BackendPayload, FormsProps, FormFieldType } from '../../types/forms';
// import { getDecisionCriteria } from '../../api/decisions.api';
import { getFormsResult, submitForm } from '../../api/forms.api';
import { transformFormFields } from '../../utils/forms.utils';
import Loading from '../loading/Loading';
import { Box, TextField, Typography } from '@mui/material';
import DynamicForm from '../DynamicForm/DynamicForm';
import { useAuth } from '../../context/auth-context';

const EDIT_AS_STRING_TYPES: FormFieldType[] = [ 'url', 'image', 'video' ] as FormFieldType[];

const DataUpdateForm = ({
    formId, formName, formAllFields: parentFormFields, responseId, onCancel, onAfterUpdate,
}: FormsProps) => {
    const { tokens: { sessionToken, reactToken } } = useAuth();
    const { showMessage, MessageContainer } = useMessage();

    const [ loading, setLoading ] = useState(true);
    const [ submitting, setSubmitting ] = useState(false);
    // TODO clarify id we need to use linked or original form configs
    const [ formAllFields, setFormAllFields ] = useState<BackendPayload['fields']>([]);
    const [ baseValues, setBaseValues ] = useState<{ [key: string]: any }>({});
    const [ manualValues, setManualValues ] = useState<{ [key: string]: any }>({});

    const editableStringFields = useMemo(() => {
        return formAllFields.filter(field => EDIT_AS_STRING_TYPES.includes(field.type));
    }, [ formAllFields ]);

    const dynamicFormFields = useMemo(() => {
        return formAllFields.filter(field => !EDIT_AS_STRING_TYPES.includes(field.type));
    }, [ formAllFields ]);

    useEffect(() => {
        if (!formId || !responseId) {
            setLoading(false);
            return;
        }

        Promise.all([
            // getDecisionCriteria(formId),
            getFormsResult(formId, responseId),
        ])
               .then(results => {
                   const [ response2 ] = results;

                   const values: { [key: string]: any; } = {};

                   let items: any[] = response2.response;

                   if (!Array.isArray(items)) {
                       // @ts-ignore
                       if (items.data) {
                           // @ts-ignore
                           items = items.data;
                       }
                   }

                   items.forEach(item => {
                       values[item.id] = item.value;
                   });

                   // const elements: any[] = response.form_config.elements;
                   const formattedElements: BackendPayload['fields'] = [];

                   formattedElements.push(...transformFormFields(parentFormFields, values));

                   const manualInitialValues: { [key: string]: any } = {};

                   // TODO update with previous todo at the top
                   formattedElements.forEach(field => {
                       if (EDIT_AS_STRING_TYPES.includes(field.type)) {
                           manualInitialValues[field.id] = values[field.id] ?? '';
                       }
                   });

                   setBaseValues(values);
                   setManualValues(manualInitialValues);
                   setFormAllFields(formattedElements);
               })
               .catch((err) => {
                   if (err.response?.status === 401) {
                       if (reactToken && !sessionToken) {
                           window.location.reload();
                       }
                   }

                   console.log(err);
               })
               .finally(() => setLoading(false));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [ formId, responseId ]);

    const submitFormHandler = async (values: {
        [key: string]: any;
    }) => {
        if (!formId || !responseId) {
            return;
        }

        const mergedValues = {
            ...baseValues,
            ...values,
            ...manualValues,
        };

        const formData = {
            form_id: +formId,
            response_id: +responseId,
            data: formAllFields.map(field => ({
                id: field.id,
                value: mergedValues[field.id] ?? '',
            })),
        };

        setSubmitting(true);

        submitForm(formData)
        .then(() => {
            showMessage('Your response was updated successfully.', 'success');
            onAfterUpdate?.(formData);
            onCancel && setTimeout(onCancel, 1500);
        })
        .catch((err) => {
            console.log(err);
        })
        .finally(() => setSubmitting(false));
    };

    const onManualValueChange = (fieldId: string, value: string) => {
        setManualValues(prev => ({
            ...prev,
            [fieldId]: value,
        }));
    };

    const manualFieldsContent = editableStringFields.length > 0 ? (
        <Box sx={{ width: '100%' }}>
            {editableStringFields.map(field => (
                <Box key={field.id} sx={{ marginBottom: '14px' }}>
                    <Typography
                        sx={{
                            fontWeight: 700,
                            fontSize: '13px',
                            marginBottom: '6px',
                        }}
                    >
                        {field.label || field.name}
                    </Typography>

                    <TextField
                        fullWidth
                        size="small"
                        value={manualValues[field.id] ?? ''}
                        onChange={(event) => onManualValueChange(field.id, event.target.value)}
                    />
                </Box>
            ))}
        </Box>
    ) : null;

    return (
        <div>
            {loading ? <Loading/> : (
                <div>
                    <Typography variant="h4" gutterBottom>
                        Edit result {responseId}
                    </Typography>

                    <DynamicForm
                        formId={formId}
                        formName={formName}
                        config={{ fields: dynamicFormFields }}
                        onSubmit={submitFormHandler}
                        apiSubmitting={submitting}
                        saveButtonLabel="Save"
                        hasCancelButton
                        onCancel={onCancel}
                        additionalTopContent={manualFieldsContent}
                    />
                </div>
            )}

            <MessageContainer/>
        </div>
    );
};

export default DataUpdateForm;